import { useState, useEffect, useCallback } from 'react';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import SmsContentStep from 'domains/campaigns/components/CustomCampaign/CustomCampaignForm/steps/CustomCampaignContentStep/SmsContentStep';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, ABTestType } from 'domains/campaigns/types';

import CustomCampaignScheduleStep from '../CustomCampaignScheduleStep/CustomCampaignScheduleStep';

import CustomCampaignChooseSendContentStep from './CustomCampaignChooseSendContentStep';
import DirectMailContentStep from './DirectMailContentStep';
import EmailContentStep from './EmailContentStep/EmailContentStep';
import SharedContentSelectionStep from './SharedContentSelectionStep';
import VoiceCallContentStep from './VoiceCallContentStep';

// True when this send has no content chosen yet — used to gate mixed-channel campaigns
// into the merged content picker instead of a channel already implied by a placeholder shape.
const isSendContentUnset = (send: any, channel: CampaignChannels): boolean =>
    channel === CampaignChannels.EMAIL ? !send.templateId : !send.content?.id;

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    initialContentSubStep?: 'selecting' | 'scheduling'; // New: for controlling initial sub-step
    setActiveSendIndex: (index: number) => void; // New: for managing active send index
    setContinueTooltip: (tooltip: string) => void;
    setContentPreviewId: (id: number) => void;
    setCustomContinueHandler?: (handler: (() => void) | null) => void; // Optional: custom continue handler
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const CustomCampaignContentStep = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    initialContentSubStep = 'selecting',
    setActiveSendIndex,
    setContinueTooltip,
    setContentPreviewId,
    setCustomContinueHandler,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    const currentSend = campaignState.sends[activeSendIndex];
    const { channel } = currentSend;

    // Sub-step state management for send-time A/B testing
    const [contentSubStep, setContentSubStep] = useState<'selecting' | 'scheduling'>(initialContentSubStep);

    // Reset sub-step when A/B test configuration changes or when not in send-time A/B testing
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;
    useEffect(() => {
        if (!isABTestingSendTime) {
            setContentSubStep('selecting');
        }
    }, [isABTestingSendTime]);

    // Memoized callback to prevent infinite re-renders
    const handleSharedContentContinue = useCallback(() => {
        setContentSubStep('scheduling');
        setActiveSendIndex(0); // Start with Email-A
    }, [setActiveSendIndex]); // Include setActiveSendIndex for ESLint compliance

    if (campaignState.isMixedChannel && isSendContentUnset(currentSend, channel)) {
        return (
            <CustomCampaignChooseSendContentStep
                activeSendIndex={activeSendIndex}
                dispatchCampaignAction={dispatchCampaignAction}
                setIsContinueEnabled={setIsContinueEnabled}
            />
        );
    }

    if (channel === CampaignChannels.EMAIL) {
        // Smart Navigation Router: For send time A/B testing
        if (isABTestingSendTime) {
            // Ensure both sends are email sends
            const send1 = campaignState.sends[0];
            const send2 = campaignState.sends[1];
            const isEmailSend1 = send1.channel === CampaignChannels.EMAIL;
            const isEmailSend2 = send2.channel === CampaignChannels.EMAIL;

            if (!isEmailSend1 || !isEmailSend2) {
                // If not both email sends, fallback to normal content step
                return (
                    <EmailContentStep
                        activeSendIndex={activeSendIndex}
                        campaignState={campaignState}
                        contentPreviewId={contentPreviewId}
                        dispatchCampaignAction={dispatchCampaignAction}
                        setContinueTooltip={setContinueTooltip}
                        setContentPreviewId={setContentPreviewId}
                        setIsContinueEnabled={setIsContinueEnabled}
                        setNextStep={setNextStep}
                        onContentUnavailableChange={onContentUnavailableChange}
                    />
                );
            }

            // Sub-step routing based on user action, not validation state
            if (contentSubStep === 'selecting') {
                // Show shared content selection step
                return (
                    <SharedContentSelectionStep
                        campaignState={campaignState}
                        contentPreviewId={contentPreviewId}
                        dispatchCampaignAction={dispatchCampaignAction}
                        setContentPreviewId={setContentPreviewId}
                        setCustomContinueHandler={setCustomContinueHandler}
                        setIsContinueEnabled={setIsContinueEnabled}
                        setNextStep={setNextStep}
                        onContinue={handleSharedContentContinue}
                    />
                );
            } else {
                // Show schedule step for specific email
                const abTestVariant = activeSendIndex === 0 ? 'A' : 'B';

                return (
                    <CustomCampaignScheduleStep
                        key={`custom-campaign-schedule-step-${activeSendIndex}`}
                        campaignState={campaignState}
                        dispatchCampaignAction={dispatchCampaignAction}
                        setActiveSendIndex={setActiveSendIndex}
                        setCustomContinueHandler={setCustomContinueHandler}
                        setIsContinueEnabled={setIsContinueEnabled}
                        setNextStep={setNextStep}
                        targetSendIndex={activeSendIndex}
                        abTestVariant={abTestVariant}
                    />
                );
            }
        }

        // Default behavior: show content selection step for content A/B testing or normal flow
        return (
            <EmailContentStep
                activeSendIndex={activeSendIndex}
                campaignState={campaignState}
                contentPreviewId={contentPreviewId}
                dispatchCampaignAction={dispatchCampaignAction}
                setContinueTooltip={setContinueTooltip}
                setContentPreviewId={setContentPreviewId}
                setIsContinueEnabled={setIsContinueEnabled}
                setNextStep={setNextStep}
                onContentUnavailableChange={onContentUnavailableChange}
            />
        );
    }

    if (channel === CampaignChannels.DIRECT_MAIL) {
        return (
            <DirectMailContentStep
                activeSendIndex={activeSendIndex}
                campaignState={campaignState}
                contentPreviewId={contentPreviewId}
                dispatchCampaignAction={dispatchCampaignAction}
                setContentPreviewId={setContentPreviewId}
                setIsContinueEnabled={setIsContinueEnabled}
                setNextStep={setNextStep}
                onContentUnavailableChange={onContentUnavailableChange}
            />
        );
    }

    if (channel === CampaignChannels.SMS) {
        return (
            <SmsContentStep
                activeSendIndex={activeSendIndex}
                campaignState={campaignState}
                contentPreviewId={contentPreviewId}
                dispatchCampaignAction={dispatchCampaignAction}
                setContentPreviewId={setContentPreviewId}
                setIsContinueEnabled={setIsContinueEnabled}
                setNextStep={setNextStep}
                onContentUnavailableChange={onContentUnavailableChange}
            />
        );
    }

    if (channel === CampaignChannels.VOICE_CALL) {
        return (
            <VoiceCallContentStep
                activeSendIndex={activeSendIndex}
                campaignState={campaignState}
                contentPreviewId={contentPreviewId}
                dispatchCampaignAction={dispatchCampaignAction}
                setContentPreviewId={setContentPreviewId}
                setIsContinueEnabled={setIsContinueEnabled}
                setNextStep={setNextStep}
                onContentUnavailableChange={onContentUnavailableChange}
            />
        );
    }
};

export default CustomCampaignContentStep;
